import styled from 'styled-components';
import {
  cssColorValue,
  semanticColors,
  TableContainer,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import {
  AppearsOnPaginationContext,
  ArtistAlbum,
} from '@hooks/useArtistAppearsOn/types';
import { AlbumsPaginationContext } from '@hooks/useArtistInfo/types';
import { RenderPaginationProps } from '@hooks/usePagination';
import { Loading } from '@components/shared/Loading';
import { LoadingError } from '@components/shared/LoadingError';
import { Table } from '@components/shared/Table';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import { EmptyState } from './EmptyState';
import { TableRow } from './TableRow';

const StyledTableContainer = styled(TableContainer)`
  min-height: 700px;
`;

const CtaContainer = styled.div`
  display: flex;
  background-color: ${cssColorValue(semanticColors.backgroundHighlight)};
  height: 316px;
  justify-content: center;
`;

export interface ReleasesTableProps {
  variant: 'releases' | 'appearsOn';
  showEmptyState: boolean;
  albums: ArtistAlbum[];
  isLoading?: boolean;
  isError?: boolean;
  paginationProps?: {
    paginationContext: AlbumsPaginationContext | AppearsOnPaginationContext;
    renderPagination: (
      renderPaginationProps: RenderPaginationProps,
    ) => JSX.Element;
  };
}

export const ReleasesTable = ({
  variant,
  showEmptyState,
  albums,
  isLoading,
  isError,
  paginationProps,
}: ReleasesTableProps) => {
  const t = useT();
  let emptyStateTitle: string = '';
  let emptyStateSubtitle: string = '';

  const handleIncrement = () => {
    sendEvent({
      eventCategory: EVENT_CATEGORY.ARTIST_RELEASES_TABLE,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: `Pagination clicked: increment`,
      product: GA_PRODUCT_CATALOG,
    });
  };

  const handleDecrement = () => {
    sendEvent({
      eventCategory: EVENT_CATEGORY.ARTIST_RELEASES_TABLE,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: `Pagination clicked: decrement`,
      product: GA_PRODUCT_CATALOG,
    });
  };

  const handleLimitChange = (value: number) => {
    sendEvent({
      eventCategory: EVENT_CATEGORY.ARTIST_RELEASES_TABLE,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: `Pagination limit changed: ${value}`,
      product: GA_PRODUCT_CATALOG,
    });
  };

  switch (variant) {
    case 'releases':
      emptyStateTitle = t(
        'atlas.empty.no_releases_yet',
        'No releases yet',
        'Indicates empty state',
      );
      emptyStateSubtitle = t(
        'atlas.artist.no_releases_associated_yet',
        `We haven't found any releases associated with this artist just yet. Deliver a release with this artist as a main artist or remixer to see it here.`,
        'Explains that there are no releases associated with the artist within Spotify yet.',
      );
      break;
    case 'appearsOn':
      emptyStateTitle = t(
        'atlas.empty.no_appearances_yet',
        'No appearances yet',
        'Title text for a section that indicates that an artist has not appeared on any releases yet',
      );
      emptyStateSubtitle = t(
        'atlas.artist.no_releases_associated_yet_for_track',
        `We haven’t found any releases associated with this artist just yet. Deliver a release with this artist as a track level main artist, featured artist or remixer to see it here.`,
        'Explains that there are no releases associated with the artist within Spotify yet.',
      );
      break;
    default:
      break;
  }

  if (isLoading) {
    return <Loading size="md" smallHeight />;
  }

  if (isError) {
    return (
      <CtaContainer>
        <LoadingError />
      </CtaContainer>
    );
  }

  if (!isLoading && !isError && showEmptyState) {
    return <EmptyState title={emptyStateTitle} subtitle={emptyStateSubtitle} />;
  }

  return (
    <StyledTableContainer>
      <Table
        widths={['20%', '8%', '7%', '12%', '12%', '14%', '23%', '4%']}
        headers={[
          t(
            'atlas.labels.release_name',
            'Release name',
            'Label for Release name',
          ),
          t(
            'atlas.labels.product_type',
            'Product type',
            'Label for Product type',
          ),
          t('atlas.labels.feed_name', 'Feed name', 'Label for Feed name'),
          t('atlas.labels.licensor', 'Licensor', 'Label for Licensor name'),
          t('atlas.labels.label', 'Label', 'Label for Label name'),
          t(
            'atlas.labels.original_release_date',
            'Original release date',
            'Label for Original release date',
          ),
          'Territories',
          'Actions',
        ]}
      >
        {albums &&
          albums.map((album: ArtistAlbum, index: number) => (
            <TableRow album={album} key={`tablerow-${index}`} />
          ))}
      </Table>
      {paginationProps?.renderPagination({
        totalResultRows:
          Number(paginationProps.paginationContext.totalRows) || 0,
        customRowsPerPageText: t(
          'atlas.table_pagination.results_per_page',
          'Results per page:',
          'Label for dropdown to select how many results per page to show',
        ),
        finalTotalResultIsKnown: true,
        isLastPage: paginationProps.paginationContext.lastPage,
        onIncrementCallback: handleIncrement,
        onDecrementCallback: handleDecrement,
        onLimitChangeCallback: handleLimitChange,
      })}
    </StyledTableContainer>
  );
};
